import React, { CSSProperties, forwardRef, RefObject } from 'react';
import {
  FormControl,
  FormHelperText,
  Input,
  InputBaseProps,
  InputLabel,
  InputLabelProps,
  styled,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import styles from './TextField.module.scss';
import cx from 'classnames';

export const CustomInput = styled(Input)(({ theme, disabled, error, color }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  backgroundColor: disabled ? theme.palette.info.light : '',
  borderRadius: 8,
  position: 'relative',
  border: '1px solid #ced4da',
  width: 'auto',
  padding: '6px 10px',
  transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
  '&:focus-within': {
    borderColor: (() => {
      if (error) {
        return theme.palette.error.main;
      }
      if (color) {
        return theme.palette[color]?.main;
      }
      return theme.palette.primary.main;
    })(),
  },
  '&:before': {
    display: 'none',
  },
  '&:after': {
    display: 'none',
  },
  '.MuiInput-input': {
    padding: 0,
  },
}));

export type TextFieldProps = {
  isRequired?: boolean;
  formControlStyle?: CSSProperties;
  inputRef?: RefObject<HTMLInputElement>;
} & InputBaseProps &
  MuiTextFieldProps &
  Pick<InputLabelProps, 'htmlFor'>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (props: TextFieldProps, ref) => {
    const {
      label,
      fullWidth,
      htmlFor,
      className,
      helperText,
      error,
      inputRef,
      formControlStyle,
      InputProps,
      InputLabelProps,
      sx,
      ...rest
    } = props;

    /**
     * isRequired is not a HTML property
     * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#required
     */
    let { isRequired, ...customInputProps } = rest as any;
    customInputProps = { ...customInputProps, required: !!isRequired };

    return (
      <FormControl
        sx={sx}
        style={formControlStyle}
        fullWidth={fullWidth}
        className={cx(className, 'MuiTextField-root')}
        error={error}
        ref={ref}
        variant="standard"
      >
        {!!label && (
          <InputLabel
            {...InputLabelProps}
            className={styles.label}
            shrink
            htmlFor={htmlFor}
            tabIndex={-1}
          >
            {label}
          </InputLabel>
        )}
        <CustomInput
          id={htmlFor}
          inputRef={inputRef}
          error={error}
          {...InputProps}
          {...customInputProps}
        />
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  },
);
